import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';

const SubscriptionActionModal = ({
  show,
  handleClose,
  onSubmit,
  subscription,
  actionType,
}) => {
  const [formData, setFormData] = useState({
    start_date: '',
    additional_days: '',
    reason: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      // Reset form data
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        start_date: today,
        additional_days: '30',
        reason: ''
      });
      setErrors({});
    }
  }, [show, actionType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (actionType === 'activate' && !formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (actionType === 'extend') {
      if (!formData.additional_days || parseInt(formData.additional_days) <= 0) {
        newErrors.additional_days = 'Additional days must be greater than 0';
      }
    }

    if (actionType === 'cancel' && !formData.reason?.trim()) {
      newErrors.reason = 'Cancellation reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSubmit = {};

      switch (actionType) {
        case 'activate':
          dataToSubmit.start_date = formData.start_date;
          break;
        case 'extend':
          dataToSubmit.additional_days = parseInt(formData.additional_days);
          break;
        case 'cancel':
          dataToSubmit.reason = formData.reason.trim();
          break;
        case 'expire':
          // No additional data needed
          break;
        default:
          break;
      }

      const result = await onSubmit(dataToSubmit);
      if (result?.success) {
        handleModalClose();
      } else if (result?.error) {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      console.error('Error performing action:', error);
      setErrors({ submit: 'Failed to perform action' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setFormData({
      start_date: '',
      additional_days: '',
      reason: ''
    });
    setErrors({});
    setIsSubmitting(false);
    handleClose();
  };

  const getActionTitle = () => {
    switch (actionType) {
      case 'activate':
        return 'Activate Subscription';
      case 'cancel':
        return 'Cancel Subscription';
      case 'expire':
        return 'Expire Subscription';
      case 'extend':
        return 'Extend Subscription';
      default:
        return 'Subscription Action';
    }
  };

  const getActionIcon = () => {
    switch (actionType) {
      case 'activate':
        return 'bi-check-circle text-success';
      case 'cancel':
        return 'bi-x-circle text-danger';
      case 'expire':
        return 'bi-clock-history text-warning';
      case 'extend':
        return 'bi-calendar-plus text-primary';
      default:
        return 'bi-gear';
    }
  };

  const getActionDescription = () => {
    switch (actionType) {
      case 'activate':
        return `Activate subscription for ${subscription?.user_mobile} on plan "${subscription?.plan_name}". This will start the subscription period.`;
      case 'cancel':
        return `Cancel subscription for ${subscription?.user_mobile}. The user will lose access to premium features.`;
      case 'expire':
        return `Mark subscription for ${subscription?.user_mobile} as expired immediately. Current end date: ${subscription?.end_date || 'Not set'}`;
      case 'extend':
        return `Extend subscription for ${subscription?.user_mobile}. Current end date: ${subscription?.end_date || 'Not set'}`;
      default:
        return '';
    }
  };

  const renderFormFields = () => {
    switch (actionType) {
      case 'activate':
        return (
          <Form.Group className="mb-3">
            <Form.Label>
              Start Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              isInvalid={!!errors.start_date}
              disabled={isSubmitting}
            />
            <Form.Control.Feedback type="invalid">
              {errors.start_date}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              The subscription will start from this date and end after the plan's duration period.
            </Form.Text>
          </Form.Group>
        );

      case 'extend':
        return (
          <Form.Group className="mb-3">
            <Form.Label>
              Additional Days <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              name="additional_days"
              value={formData.additional_days}
              onChange={handleChange}
              isInvalid={!!errors.additional_days}
              disabled={isSubmitting}
              min="1"
              placeholder="Enter number of days"
            />
            <Form.Control.Feedback type="invalid">
              {errors.additional_days}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              The subscription end date will be extended by this many days.
            </Form.Text>
          </Form.Group>
        );

      case 'cancel':
        return (
          <Form.Group className="mb-3">
            <Form.Label>
              Cancellation Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              isInvalid={!!errors.reason}
              disabled={isSubmitting}
              placeholder="Enter the reason for cancellation"
            />
            <Form.Control.Feedback type="invalid">
              {errors.reason}
            </Form.Control.Feedback>
          </Form.Group>
        );

      case 'expire':
        return (
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            This action will immediately expire the subscription. The user will lose access to premium features.
          </Alert>
        );

      default:
        return null;
    }
  };

  const getSubmitButtonVariant = () => {
    switch (actionType) {
      case 'activate':
        return 'success';
      case 'cancel':
      case 'expire':
        return 'danger';
      case 'extend':
        return 'primary';
      default:
        return 'primary';
    }
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`bi ${getActionIcon()} me-2`}></i>
          {getActionTitle()}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.submit && (
            <Alert variant="danger" className="mb-3">{errors.submit}</Alert>
          )}

          {/* Subscription Info */}
          {subscription && (
            <div className="bg-light p-3 rounded mb-3">
              <Row>
                <Col md={6}>
                  <small className="text-muted">User</small>
                  <div><strong>{subscription.user_mobile}</strong></div>
                  {subscription.user_name && (
                    <div className="text-muted small">{subscription.user_name}</div>
                  )}
                </Col>
                <Col md={6}>
                  <small className="text-muted">Plan</small>
                  <div><strong>{subscription.plan_name}</strong></div>
                </Col>
              </Row>
            </div>
          )}

          <p className="text-muted mb-3">{getActionDescription()}</p>

          {renderFormFields()}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
          <Button
            type="submit"
            variant={getSubmitButtonVariant()}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : `Confirm ${getActionTitle().split(' ')[0]}`}
          </Button>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubscriptionActionModal;
